import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ConsentContext = createContext(null);
const STORAGE_KEY = 'vs-cookie-consent';

const DEFAULT_CONSENT = { necessary: true, statistics: false, marketing: false };

function loadStoredConsent() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_CONSENT, ...parsed, necessary: true };
  } catch {
    return null;
  }
}

/**
 * Consenso cookie GDPR — blocco reale, non solo il banner: finché l'utente
 * non decide, `consent.statistics`/`consent.marketing` restano `false` e
 * qualsiasi contenuto avvolto in `<ConsentGate category="...">` non viene
 * montato affatto (niente script caricati "silenziosamente" in attesa).
 * `necessary` è sempre true (cookie tecnici, nessuna base per rifiutarli).
 * La mappa OpenStreetMap (blocco Mappa) NON passa da qui: i tile OSM non
 * impostano cookie di tracciamento, non serve consenso — vedi _docs/decisions.md.
 */
export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [decided, setDecided] = useState(true); // true finché non sappiamo che serve chiedere, evita un flash del banner
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    const stored = loadStoredConsent();
    setConsent(stored || DEFAULT_CONSENT);
    setDecided(!!stored);
  }, []);

  const persist = useCallback((next) => {
    setConsent(next);
    setDecided(true);
    setPreferencesOpen(false);
    if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, []);

  const acceptAll = useCallback(() => persist({ necessary: true, statistics: true, marketing: true }), [persist]);
  const rejectNonEssential = useCallback(() => persist({ necessary: true, statistics: false, marketing: false }), [persist]);
  const savePreferences = useCallback((partial) => persist({ ...consent, ...partial, necessary: true }), [consent, persist]);
  const openPreferences = useCallback(() => setPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setPreferencesOpen(false), []);

  const value = useMemo(
    () => ({ consent, decided, preferencesOpen, acceptAll, rejectNonEssential, savePreferences, openPreferences, closePreferences }),
    [consent, decided, preferencesOpen, acceptAll, rejectNonEssential, savePreferences, openPreferences, closePreferences]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent va usato dentro <ConsentProvider>');
  return ctx;
}

/** Monta i figli solo se la categoria è stata accettata — blocco reale per script/embed non essenziali (analytics, pixel di marketing...). */
export function ConsentGate({ category, children }) {
  const { consent } = useConsent();
  return consent[category] ? children : null;
}
